import React from 'react';
import {
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  useTheme,
  Box,
} from '@mui/material';
import {
  School as SchoolIcon,
  People as PeopleIcon,
  Class as ClassIcon,
  EventNote as EventNoteIcon,
  BarChart as BarChartIcon,
  MenuBook as MenuBookIcon,
  Assignment as AssignmentIcon,
  Payment as PaymentIcon,
  Backup as BackupIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useResponsive } from '../../hooks/useResponsive';
import IOSStyleButton from './IOSStyleButton';

interface ResponsiveBottomNavigationProps {
  currentPage: string;
  onPageChange: (page: string) => void;
}

const ResponsiveBottomNavigation: React.FC<ResponsiveBottomNavigationProps> = ({
  currentPage,
  onPageChange,
}) => {
  const theme = useTheme();
  const { screenWidth } = useResponsive();

  // 根据侧边栏菜单项定义完整的导航项
  const navigationItems = [
    {
      label: '学年',
      value: 'schoolYears',
      icon: <SchoolIcon />,
      backgroundColor: '#007AFF',
    },
    {
      label: '教师',
      value: 'teachers',
      icon: <PeopleIcon />,
      backgroundColor: '#FF9500',
    },
    {
      label: '学生',
      value: 'students',
      icon: <PeopleIcon />,
      backgroundColor: '#34C759',
    },
    {
      label: '班级',
      value: 'classes',
      icon: <ClassIcon />,
      backgroundColor: '#FF3B30',
    },
    {
      label: '科目',
      value: 'subjects',
      icon: <MenuBookIcon />,
      backgroundColor: '#AF52DE',
    },
    {
      label: '考勤',
      value: 'attendance',
      icon: <EventNoteIcon />,
      backgroundColor: '#FF2D92',
    },
    {
      label: '成绩',
      value: 'grades',
      icon: <AssignmentIcon />,
      backgroundColor: '#5AC8FA',
    },
    {
      label: '缴费',
      value: 'payments',
      icon: <PaymentIcon />,
      backgroundColor: '#FFCC00',
    },
    {
      label: '统计',
      value: 'statistics',
      icon: <BarChartIcon />,
      backgroundColor: '#FF6B6B',
    },
    {
      label: '备份',
      value: 'backup',
      icon: <BackupIcon />,
      backgroundColor: '#4ECDC4',
    },
    {
      label: '调试',
      value: 'debug',
      icon: <RefreshIcon />,
      backgroundColor: '#96CEB4',
    },
  ];

  // 小屏幕设备显示底部导航：屏幕宽度小于600px的设备
  const shouldShowBottomNav = screenWidth < 600;
  
  // 如果不应该显示底部导航，直接返回null
  if (!shouldShowBottomNav) {
    return null;
  }

  const handleChange = (value: string) => {
    onPageChange(value);
  };

  // 当显示底部导航栏时就使用iOS风格（小于600px）
  const useIOSStyle = shouldShowBottomNav;

  if (useIOSStyle) {
    return (
      <Paper
        className="ios-bottom-nav"
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: theme.zIndex.appBar,
        }}
        elevation={8}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 1,
            padding: 1.5,
            paddingBottom: 2,
            minHeight: 70,
            maxHeight: 100,
            overflowX: 'auto',
            overflowY: 'hidden',
            alignItems: 'start',
            justifyContent: 'flex-start',
            // 支持滚动查看更多按钮
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            scrollbarWidth: 'none',
          }}
        >
          {navigationItems.map((item, index) => (
            <IOSStyleButton
              key={item.value}
              icon={item.icon}
              label={item.label}
              selected={currentPage === item.value}
              onClick={() => handleChange(item.value)}
              size="unified"
              backgroundColor={item.backgroundColor}
              sx={{
                minWidth: 'fit-content',
                flexShrink: 0,
              }}
            />
          ))}
        </Box>
      </Paper>
    );
  }

  // 普通Material-UI底部导航（适用于中等尺寸屏幕）
  return (
    <Paper
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: theme.zIndex.appBar,
        borderTop: `1px solid ${theme.palette.divider}`,
      }}
      elevation={8}
    >
      <BottomNavigation
        value={currentPage}
        onChange={(event, newValue) => handleChange(newValue)}
        sx={{
          height: 64,
          overflowX: 'auto',
          overflowY: 'hidden',
          display: 'flex',
          justifyContent: 'flex-start',
          '&::-webkit-scrollbar': {
            height: 2,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(0,0,0,0.1)',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.primary.main,
            borderRadius: 1,
          },
          '& .MuiBottomNavigation-root': {
            minWidth: 'max-content',
          },
          '& .MuiBottomNavigationAction-root': {
            minWidth: 'auto',
            padding: '4px 6px 6px',
            fontSize: '0.65rem',
            flex: 'none',
            width: `${100 / navigationItems.length}%`,
            maxWidth: 'none',
            '&.Mui-selected': {
              color: theme.palette.primary.main,
              '& .MuiBottomNavigationAction-label': {
                fontSize: '0.65rem',
              },
            },
          },
          '& .MuiBottomNavigationAction-label': {
            fontSize: '0.60rem',
            lineHeight: 1.1,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%',
            marginTop: '2px',
          },
          '& .MuiBottomNavigationAction-icon': {
            fontSize: '1.1rem',
            marginBottom: '2px',
          },
        }}
      >
        {navigationItems.map((item) => (
          <BottomNavigationAction
            key={item.value}
            label={item.label}
            value={item.value}
            icon={item.icon}
            sx={{
              minWidth: 'auto',
              flex: '0 0 auto',
              width: `calc(100% / ${navigationItems.length})`,
            }}
          />
        ))}
      </BottomNavigation>
      
      {/* 为底部导航留出空间的占位符 */}
      <Box sx={{ height: 0 }} />
    </Paper>
  );
};

export default ResponsiveBottomNavigation; 